import React, { useEffect } from 'react';
import s from './s.module.less';
import AIChat from 'components/AIChat';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import GoogleSync from './components/GoogleSync';
import ProfileEnrich from './components/ProfileEnrich';
import useGoogleAuthStatus from 'hooks/useGoogleAuthStatus';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import useLoadBasicData from 'hooks/useLoadBasicData';
import { PATH } from 'constants/path';
import PayoutAccountSetup from './components/PayoutAccountSetup';
import ReviewSettings from './components/ReviewSettings';
import { getUrlParam } from 'utils/common';
import { trackHeapEvent } from 'utils/analytics';
import ProlistingSetup from './components/ProlistingSetup';
import useGetOffForProlisting from 'hooks/useGetOffForProlisting';
import { EAIChatPageId } from 'types/ai';
import { EKey, getUserId } from 'utils/localstore';
import useProviderOverview from 'hooks/useProviderOverview';
import StripePayoutAccountSetup from './components/PayoutAccountSetup/StripePayoutAccountSetup';

const LOCAL_STORAGE_KEY_PREFIX = EKey.PPA_ADVANCE_SETTING_STEP;
const getLocalStorageKey = () => `${LOCAL_STORAGE_KEY_PREFIX}_${getUserId()}`;

enum EStep {
    INIT = 'init',
    PROFILE_ENRICH = 'profile_enrich',
    PROLISTING_SETUP = 'prolisting_setup',
    GOOGLE_SYNC = 'google_sync',
    PAYOUT_ACCOUNT = 'payout_account',
    REVIEW = 'review',
    //NO_SHOW_POLICY = 'no_show_policy',
}

export default function PPAOnboardingAdvanceSettingPage() {
    const isValidStep = (val?: string | null): val is EStep => {
        if (val) return Object.values(EStep).includes(val as EStep);
        return false;
    };

    const [step, setStep] = React.useState<EStep>(() => {
        const tag = getUrlParam('tab');
        if (isValidStep(tag)) {
            return tag;
        }
        const savedStep = localStorage.getItem(getLocalStorageKey());
        if (isValidStep(savedStep)) {
            return savedStep;
        }
        return EStep.INIT;
    });
    //after auth success, should set step to NO_SHOW_POLICY
    useGoogleAuthStatus();
    const navigate = useNavigate();
    const [basicAPICalling] = useLoadBasicData();
    const [off] = useGetOffForProlisting();
    const [providerOverview] = useProviderOverview();
    const useStripePayoutUi = providerOverview?.hasStripeConnectAccount === true;

    useEffect(() => {
        basicAPICalling();
    }, [basicAPICalling]);

    const Steps = [
        EStep.INIT,
        EStep.PROFILE_ENRICH,
        EStep.PROLISTING_SETUP,
        EStep.GOOGLE_SYNC,
        EStep.PAYOUT_ACCOUNT,
        EStep.REVIEW,
    ];

    const handleNext = () => {
        const nextStep = Steps[Steps.indexOf(step) + 1];
        trackHeapEvent('WebApp - PPA Advance Setting - Click - Next', {
            fromStep: step,
            toStep: nextStep,
        });
        if (nextStep) {
            setStep(nextStep);
        } else {
            localStorage.removeItem(getLocalStorageKey());
            navigate(PATH.PPA_ONBOARDING_ADVANCE_SETTING_SUCCESS);
        }
    };

    const handleBack = () => {
        const prevStep = Steps[Steps.indexOf(step) - 1];
        trackHeapEvent('WebApp - PPA Advance Setting - Click - Back', {
            fromStep: step,
            toStep: prevStep,
        });
        setStep(prevStep);
    };

    React.useEffect(() => {
        const tag = getUrlParam('tab');
        if (tag === EStep.GOOGLE_SYNC) {
            trackHeapEvent('WebApp - PPA Advance Setting - View - Direct to Google Sync', {
                fromUrl: 'tab',
            });
            setStep(EStep.GOOGLE_SYNC);
        }
        if (tag === EStep.PAYOUT_ACCOUNT) {
            trackHeapEvent('WebApp - PPA Advance Setting - View - Direct to Payout Account', {
                fromUrl: 'tab',
            });
            setStep(EStep.PAYOUT_ACCOUNT);
        }
    }, []);

    React.useEffect(() => {
        localStorage.setItem(getLocalStorageKey(), step);
    }, [step]);

    // 根据步骤获取对应的 pageId
    const getAIChatPageId = React.useMemo((): EAIChatPageId => {
        switch (step) {
            case EStep.INIT:
                return EAIChatPageId.PROVIDER_ONBOARDING_ADVANCE_SETTING_INTRO;
            case EStep.PROFILE_ENRICH:
                return EAIChatPageId.PROVIDER_ONBOARDING_ADVANCE_SETTING_PROFILE_ENRICH;
            case EStep.PROLISTING_SETUP:
                return EAIChatPageId.PROVIDER_ONBOARDING_ADVANCE_SETTING_PROLISTING_SETUP;
            case EStep.GOOGLE_SYNC:
                return EAIChatPageId.PROVIDER_ONBOARDING_ADVANCE_SETTING_GOOGLE_SYNC;
            case EStep.PAYOUT_ACCOUNT:
            case EStep.REVIEW:
                return EAIChatPageId.PROVIDER_ONBOARDING_ADVANCE_SETTING_GOOGLE_SYNC;
            default:
                return EAIChatPageId.PROVIDER_ONBOARDING_ADVANCE_SETTING_INTRO;
        }
    }, [step]);

    return (
        <div className={s.wrap}>
            <PPAOnboardingHeader showLogout />
            <div className={s.content}>
                <div className={s.contentWrap}>
                    {step === EStep.INIT && (
                        <div className={`${s.contentIn} ${s.contentInInit}`}>
                            <h2>Set up your advanced settings</h2>
                            <div className={s.desc}>
                                Make your profile more marketable to patients and leverage advanced features to
                                streamline your operations.
                            </div>
                            <div className={s.navWrap}>
                                <div className={s.navItem}>
                                    <div className={s.navInx}>1</div>
                                    Enrich marketing profile
                                </div>
                                <svg
                                    width="1"
                                    height="18"
                                    viewBox="0 0 1 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <line
                                        x1="0.5"
                                        y1="-2.18558e-08"
                                        x2="0.500001"
                                        y2="18"
                                        stroke="#9CA3AF"
                                        strokeDasharray="2 2"
                                    />
                                </svg>

                                <div className={s.navItem}>
                                    <div className={s.navInx}>2</div>
                                    Set up pro-listings
                                    <div className={s.option}>Optional</div>
                                    <div className={s.info}>🎁 ${off} free credit available</div>
                                </div>
                                <svg
                                    width="1"
                                    height="18"
                                    viewBox="0 0 1 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <line
                                        x1="0.5"
                                        y1="-2.18558e-08"
                                        x2="0.500001"
                                        y2="18"
                                        stroke="#9CA3AF"
                                        strokeDasharray="2 2"
                                    />
                                </svg>

                                <div className={s.navItem}>
                                    <div className={s.navInx}>3</div>
                                    Sync Google calendar
                                    <div className={s.option}>Optional</div>
                                </div>
                                <svg
                                    width="1"
                                    height="18"
                                    viewBox="0 0 1 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <line
                                        x1="0.5"
                                        y1="-2.18558e-08"
                                        x2="0.500001"
                                        y2="18"
                                        stroke="#9CA3AF"
                                        strokeDasharray="2 2"
                                    />
                                </svg>

                                <div className={s.navItem}>
                                    <div className={s.navInx}>4</div>
                                    Set up payout account
                                    <div className={s.option}>Optional</div>
                                </div>
                            </div>
                            <div className={s.btnWrap}>
                                <Button
                                    onClick={() => {
                                        trackHeapEvent('WebApp - PPA Advance Setting - Click - Get Started', {
                                            step: EStep.INIT,
                                        });
                                        handleNext();
                                    }}
                                    type="primary"
                                >
                                    Get started
                                </Button>
                            </div>
                        </div>
                    )}
                    {step !== EStep.INIT && step !== EStep.REVIEW && (
                        <div className={s.contentIn}>
                            <h3>Advanced settings </h3>
                            <div className={s.navWrap}>
                                <div className={`${s.navItem} ${step === EStep.PROFILE_ENRICH ? s.navItemActive : ''}`}>
                                    <div className={s.navInx}>1</div>
                                    Enrich marketing profile
                                </div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="52"
                                    height="2"
                                    viewBox="0 0 52 2"
                                    fill="none"
                                >
                                    <line y1="1" x2="52" y2="1" stroke="#1F2937" />
                                </svg>
                                <div
                                    className={`${s.navItem} ${step === EStep.PROLISTING_SETUP ? s.navItemActive : ''}`}
                                >
                                    <div className={s.navInx}>2</div>
                                    Set up pro-listings
                                    <div className={s.option}>Optional</div>
                                </div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="52"
                                    height="2"
                                    viewBox="0 0 52 2"
                                    fill="none"
                                >
                                    <line y1="1" x2="52" y2="1" stroke="#1F2937" />
                                </svg>
                                <div className={`${s.navItem} ${step === EStep.GOOGLE_SYNC ? s.navItemActive : ''}`}>
                                    <div className={s.navInx}>3</div>
                                    Sync Google calendar
                                    <div className={s.option}>Optional</div>
                                </div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="52"
                                    height="2"
                                    viewBox="0 0 52 2"
                                    fill="none"
                                >
                                    <line y1="1" x2="52" y2="1" stroke="#1F2937" />
                                </svg>
                                <div className={`${s.navItem} ${step === EStep.PAYOUT_ACCOUNT ? s.navItemActive : ''}`}>
                                    <div className={s.navInx}>4</div>
                                    Set up payout account
                                    <div className={s.option}>Optional</div>
                                </div>
                            </div>
                            {step === EStep.PROFILE_ENRICH && <ProfileEnrich onNext={handleNext} onBack={handleBack} />}
                            {step === EStep.GOOGLE_SYNC && <GoogleSync onNext={handleNext} onBack={handleBack} />}
                            {step === EStep.PROLISTING_SETUP && (
                                <ProlistingSetup onNext={handleNext} onBack={handleBack} />
                            )}
                            {step === EStep.PAYOUT_ACCOUNT && (
                                <>
                                    {useStripePayoutUi ? (
                                        <StripePayoutAccountSetup onNext={handleNext} onBack={handleBack} />
                                    ) : (
                                        <PayoutAccountSetup onNext={handleNext} onBack={handleBack} />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    {step === EStep.REVIEW && (
                        <div className={s.contentIn}>
                            <ReviewSettings
                                onBack={handleBack}
                                onSubmit={handleNext}
                                onGoToPayoutSetup={() => setStep(EStep.PAYOUT_ACCOUNT)}
                                hasStripeConnectAccount={useStripePayoutUi}
                            />
                        </div>
                    )}
                    <div className={s.chatWrap}>
                        <AIChat pageId={getAIChatPageId} />
                    </div>
                </div>
            </div>
        </div>
    );
}
